import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { calculateTax, formatCurrency, formatPercentage, TaxCalculationInput, TaxCalculationResult, extractTaxDocumentData, STATE_TAX_DATA } from '../../utils/taxCalculator';
import { supabase } from '../../lib/supabase';

export default function TaxCalculatorPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Free Tax Liability Calculator 2024-2025 | Bold Plans Advisory Jacksonville FL';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Use Bold Plans Advisory\'s free IRS-compliant tax calculator for 2024 and 2025. Calculate federal and state tax liability, upload W-2 and 1099 documents, and get instant results.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/tax-calculator`);

    const scriptId = 'schema-tax-calc';
    document.getElementById(scriptId)?.remove();
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Free Tax Liability Calculator",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/tax-calculator`,
      "description": "IRS-compliant federal and state tax liability calculator for 2024 and 2025 tax years. Supports W-2, 1099, and K-1 document uploads.",
      "applicationCategory": "FinanceApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "provider": {
        "@type": "LocalBusiness",
        "name": "Bold Plans Advisory",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Jacksonville",
          "addressRegion": "FL",
          "addressCountry": "US"
        }
      }
    });
    document.head.appendChild(script);
    return () => { document.getElementById(scriptId)?.remove(); };
  }, []);

  const [calculationMethod, setCalculationMethod] = useState<'manual' | 'upload'>('manual');
  const [step, setStep] = useState<'input' | 'upload' | 'result' | 'id-verification'>('input');
  const [uploading, setUploading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [extractionResults, setExtractionResults] = useState<string[]>([]);
  
  const [formData, setFormData] = useState<TaxCalculationInput>({
    taxYear: 2025,
    filingStatus: 'single',
    grossIncome: 0,
    additionalIncome: 0,
    preTaxDeductions: 0,
    postTaxDeductions: 0,
    wages: 0,
    interest: 0,
    dividends: 0,
    businessIncome: 0,
    capitalGains: 0,
    otherIncome: 0,
    itemizedDeductions: 0,
    dependents: 0,
    childTaxCredit: false,
    earnedIncomeCredit: false,
    educationCredit: 0,
    retirementCredit: 0,
    otherCredits: 0,
    stateOfResidence: 'CA',
    federalTaxWithheld: 0,
  });
  
  const [result, setResult] = useState<TaxCalculationResult | null>(null);
  const [uploadedDocs, setUploadedDocs] = useState<{file: File, type: string}[]>([]);
  const [idDocument, setIdDocument] = useState<File | null>(null);

  const handleInputChange = (field: keyof TaxCalculationInput, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateTotalIncome = () => {
    const gross = (formData.wages || 0);
    const additional = (formData.interest || 0) + 
                      (formData.dividends || 0) + 
                      (formData.businessIncome || 0) + 
                      (formData.capitalGains || 0) + 
                      (formData.otherIncome || 0);
    setFormData(prev => ({ 
      ...prev, 
      grossIncome: gross,
      additionalIncome: additional 
    }));
  };

  const handleCalculate = () => {
    setCalculating(true);
    try {
      const calculationResult = calculateTax(formData);
      setResult(calculationResult);
      setStep('result');
    } catch (error) {
      console.error('Calculation error:', error);
      alert('Error calculating tax. Please check your inputs.');
    } finally {
      setCalculating(false);
    }
  };

  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>, docType: string) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const file = files[0];
    
    try {
      // Simulate OCR extraction (in production, use Azure Document Intelligence or similar)
      // For demonstration, we'll simulate extracted data based on document type
      const simulatedOCRText = generateSimulatedOCR(docType);
      const extractedData = extractTaxDocumentData(docType, simulatedOCRText);
      
      // Update form data with extracted values
      setFormData(prev => ({
        ...prev,
        wages: (prev.wages || 0) + (extractedData.wages || 0),
        federalTaxWithheld: (prev.federalTaxWithheld || 0) + (extractedData.federalWithheld || 0),
        preTaxDeductions: (prev.preTaxDeductions || 0) + (extractedData.preTaxDeductions || 0),
        interest: (prev.interest || 0) + (extractedData.interest || 0),
        dividends: (prev.dividends || 0) + (extractedData.dividends || 0),
        businessIncome: (prev.businessIncome || 0) + (extractedData.businessIncome || 0),
        otherIncome: (prev.otherIncome || 0) + (extractedData.otherIncome || 0),
      }));
      
      // Add to uploaded docs list
      setUploadedDocs(prev => [...prev, { file, type: docType }]);
      
      // Show extraction results
      if (extractedData.extractedFields.length > 0) {
        setExtractionResults(prev => [
          ...prev,
          `✓ ${docType}: Extracted ${extractedData.extractedFields.join(', ')}`
        ]);
      }
      
      if (extractedData.warnings.length > 0) {
        alert(extractedData.warnings.join('\n'));
      }
      
      // Recalculate totals
      setTimeout(() => {
        calculateTotalIncome();
      }, 100);
      
    } catch (error) {
      console.error('Document processing error:', error);
      alert('Error processing document. Please try again or enter data manually.');
    } finally {
      setUploading(false);
    }
  };

  // Simulate OCR text for demonstration
  const generateSimulatedOCR = (docType: string): string => {
    const templates: Record<string, string> = {
      'W2': `Box 1: $${(Math.random() * 80000 + 30000).toFixed(2)}\nBox 2: $${(Math.random() * 8000 + 3000).toFixed(2)}\nBox 12: $${(Math.random() * 5000).toFixed(2)}`,
      '1099-NEC': `Box 1: $${(Math.random() * 50000 + 10000).toFixed(2)}`,
      '1099-INT': `Box 1: $${(Math.random() * 2000 + 100).toFixed(2)}`,
      '1099-DIV': `Box 1a: $${(Math.random() * 3000 + 200).toFixed(2)}`,
      '1099-MISC': `Box 3: $${(Math.random() * 5000 + 500).toFixed(2)}`,
      'K1': `Ordinary business income: $${(Math.random() * 30000 + 5000).toFixed(2)}\nInterest income: $${(Math.random() * 1000).toFixed(2)}\nDividend income: $${(Math.random() * 1500).toFixed(2)}`,
    };
    return templates[docType] || '';
  };

  const handleIdUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIdDocument(file);
    setUploading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        alert('Please sign in to continue');
        navigate('/client-portal');
        return;
      }

      // Upload ID document to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-id-${Date.now()}.${fileExt}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('documents')
        .upload(`id-verification/${fileName}`, file);

      if (uploadError) throw uploadError;

      // Create verification record
      const { error: verifyError } = await supabase
        .from('id_verification')
        .insert({
          client_id: user.id,
          document_url: uploadData.path,
          verification_status: 'pending',
          extracted_data: {
            name: 'Pending Verification',
            dateOfBirth: 'Pending',
            idNumber: 'Pending',
          },
        });

      if (verifyError) throw verifyError;

      // Save tax calculation to database
      if (result) {
        await supabase.from('tax_calculations').insert({
          client_id: user.id,
          tax_year: result.taxYear,
          filing_status: result.filingStatus,
          total_income: result.totalIncome,
          deductions: result.deductionUsed,
          taxable_income: result.taxableIncome,
          tax_liability: result.finalTaxOwed,
          calculation_data: result,
        });
      }

      alert('ID verification submitted successfully! You can now access your client portal.');
      navigate('/client-portal');
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading ID. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const stateOptions = Object.entries(STATE_TAX_DATA).map(([code, data]) => ({
    code,
    name: data.name,
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <i className="ri-calculator-line text-white text-xl"></i>
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Tax Liability Calculator</h1>
              <p className="text-sm text-slate-600">IRS-Compliant Federal & State Tax Estimation</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 text-slate-600 hover:text-slate-900 font-medium transition-colors whitespace-nowrap"
          >
            <i className="ri-home-line mr-2"></i>
            Back to Home
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {step === 'input' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-3">Calculate Your Tax Liability</h2>
              <p className="text-slate-600 text-lg">Choose how you'd like to calculate your taxes</p>
            </div>

            {/* Method Selection */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <button
                onClick={() => setCalculationMethod('manual')}
                className={`p-6 rounded-xl border-2 transition-all ${
                  calculationMethod === 'manual'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-edit-line text-emerald-600 text-2xl"></i>
                </div>
                <h3 className="font-bold text-lg mb-2">Manual Entry</h3>
                <p className="text-slate-600 text-sm">Enter your income and deduction information manually</p>
              </button>

              <button
                onClick={() => setCalculationMethod('upload')}
                className={`p-6 rounded-xl border-2 transition-all ${
                  calculationMethod === 'upload'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-upload-cloud-line text-blue-600 text-2xl"></i>
                </div>
                <h3 className="font-bold text-lg mb-2">Upload Documents</h3>
                <p className="text-slate-600 text-sm">Upload W-2, 1099, K-1 and other tax documents for automatic extraction</p>
              </button>
            </div>

            {calculationMethod === 'manual' ? (
              <div className="space-y-6">
                {/* Tax Year, Filing Status, and State */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Tax Year</label>
                    <select
                      value={formData.taxYear}
                      onChange={(e) => handleInputChange('taxYear', parseInt(e.target.value))}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                      <option value={2024}>2024</option>
                      <option value={2025}>2025</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Filing Status</label>
                    <select
                      value={formData.filingStatus}
                      onChange={(e) => handleInputChange('filingStatus', e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                      <option value="single">Single</option>
                      <option value="marriedJoint">Married Filing Jointly</option>
                      <option value="marriedSeparate">Married Filing Separately</option>
                      <option value="headOfHousehold">Head of Household</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">State of Residence</label>
                    <select
                      value={formData.stateOfResidence}
                      onChange={(e) => handleInputChange('stateOfResidence', e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                      {stateOptions.map(state => (
                        <option key={state.code} value={state.code}>{state.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Income Sources */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <i className="ri-money-dollar-circle-line text-emerald-600"></i>
                    Income Sources
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Wages (W-2)</label>
                      <input
                        type="number"
                        value={formData.wages || ''}
                        onChange={(e) => handleInputChange('wages', parseFloat(e.target.value) || 0)}
                        onBlur={calculateTotalIncome}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Interest Income (1099-INT)</label>
                      <input
                        type="number"
                        value={formData.interest || ''}
                        onChange={(e) => handleInputChange('interest', parseFloat(e.target.value) || 0)}
                        onBlur={calculateTotalIncome}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Dividends (1099-DIV)</label>
                      <input
                        type="number"
                        value={formData.dividends || ''}
                        onChange={(e) => handleInputChange('dividends', parseFloat(e.target.value) || 0)}
                        onBlur={calculateTotalIncome}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Business Income (1099-NEC, K-1)</label>
                      <input
                        type="number"
                        value={formData.businessIncome || ''}
                        onChange={(e) => handleInputChange('businessIncome', parseFloat(e.target.value) || 0)}
                        onBlur={calculateTotalIncome}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Capital Gains</label>
                      <input
                        type="number"
                        value={formData.capitalGains || ''}
                        onChange={(e) => handleInputChange('capitalGains', parseFloat(e.target.value) || 0)}
                        onBlur={calculateTotalIncome}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Other Income (1099-MISC)</label>
                      <input
                        type="number"
                        value={formData.otherIncome || ''}
                        onChange={(e) => handleInputChange('otherIncome', parseFloat(e.target.value) || 0)}
                        onBlur={calculateTotalIncome}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-lg">Total Income:</span>
                      <span className="text-2xl font-bold text-emerald-600">{formatCurrency(formData.grossIncome + formData.additionalIncome)}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions */}
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <i className="ri-subtract-line text-blue-600"></i>
                    Deductions & Adjustments
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Pre-Tax Deductions (401k, HSA, etc.)</label>
                      <input
                        type="number"
                        value={formData.preTaxDeductions || ''}
                        onChange={(e) => handleInputChange('preTaxDeductions', parseFloat(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Itemized Deductions (Optional)</label>
                      <input
                        type="number"
                        value={formData.itemizedDeductions || ''}
                        onChange={(e) => handleInputChange('itemizedDeductions', parseFloat(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Leave blank for standard"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Federal Tax Withheld</label>
                      <input
                        type="number"
                        value={formData.federalTaxWithheld || ''}
                        onChange={(e) => handleInputChange('federalTaxWithheld', parseFloat(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                {/* Credits */}
                <div className="bg-purple-50 rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <i className="ri-gift-line text-purple-600"></i>
                    Tax Credits
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Number of Dependents</label>
                      <input
                        type="number"
                        value={formData.dependents || ''}
                        onChange={(e) => handleInputChange('dependents', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Education Credit</label>
                      <input
                        type="number"
                        value={formData.educationCredit || ''}
                        onChange={(e) => handleInputChange('educationCredit', parseFloat(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Retirement Savings Credit</label>
                      <input
                        type="number"
                        value={formData.retirementCredit || ''}
                        onChange={(e) => handleInputChange('retirementCredit', parseFloat(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Other Credits</label>
                      <input
                        type="number"
                        value={formData.otherCredits || ''}
                        onChange={(e) => handleInputChange('otherCredits', parseFloat(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.childTaxCredit}
                        onChange={(e) => handleInputChange('childTaxCredit', e.target.checked)}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-slate-700">Claim Child Tax Credit</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.earnedIncomeCredit}
                        onChange={(e) => handleInputChange('earnedIncomeCredit', e.target.checked)}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-slate-700">Claim Earned Income Credit</span>
                    </label>
                  </div>
                </div>

                <button
                  onClick={handleCalculate}
                  disabled={calculating || (formData.grossIncome + formData.additionalIncome) === 0}
                  className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {calculating ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Calculating...
                    </>
                  ) : (
                    <>
                      <i className="ri-calculator-line mr-2"></i>
                      Calculate Tax Liability
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
                  <div className="flex gap-3">
                    <i className="ri-shield-check-line text-amber-600 text-xl flex-shrink-0"></i>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-2">IRS-Grade Security & Data Protection</h4>
                      <ul className="text-sm text-slate-700 space-y-1">
                        <li>• Documents processed locally in your browser session</li>
                        <li>• Only income and withholding data extracted</li>
                        <li>• No SSNs, addresses, or personal identifiers collected</li>
                        <li>• Documents never stored or transmitted</li>
                        <li>• All data deleted immediately after extraction</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Document Upload Sections */}
                <div className="space-y-4">
                  {['W2', '1099-NEC', '1099-INT', '1099-DIV', '1099-MISC', 'K1'].map((docType) => (
                    <div key={docType} className="border-2 border-dashed border-slate-300 rounded-xl p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <i className="ri-file-text-line text-blue-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-900">{docType}</h4>
                            <p className="text-sm text-slate-600">
                              {docType === 'W2' && 'Wages and federal withholding'}
                              {docType === '1099-NEC' && 'Nonemployee compensation'}
                              {docType === '1099-INT' && 'Interest income'}
                              {docType === '1099-DIV' && 'Dividend income'}
                              {docType === '1099-MISC' && 'Miscellaneous income'}
                              {docType === 'K1' && 'Partnership/S-Corp income'}
                            </p>
                          </div>
                        </div>
                        <div>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleDocumentUpload(e, docType)}
                            className="hidden"
                            id={`upload-${docType}`}
                          />
                          <label
                            htmlFor={`upload-${docType}`}
                            className="inline-block px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 cursor-pointer transition-colors whitespace-nowrap text-sm"
                          >
                            <i className="ri-upload-2-line mr-2"></i>
                            Upload
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {uploading && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <div className="flex items-center justify-center gap-3 text-blue-600">
                      <i className="ri-loader-4-line animate-spin text-2xl"></i>
                      <span className="font-medium">Extracting data from document...</span>
                    </div>
                  </div>
                )}

                {uploadedDocs.length > 0 && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                    <h4 className="font-bold mb-3 flex items-center gap-2">
                      <i className="ri-check-line text-emerald-600"></i>
                      Uploaded & Processed Documents
                    </h4>
                    <div className="space-y-2">
                      {uploadedDocs.map((doc, idx) => (
                        <div key={idx} className="flex items-center gap-3 bg-white p-3 rounded-lg">
                          <i className="ri-file-text-line text-emerald-600"></i>
                          <span className="text-sm font-medium">{doc.type}</span>
                          <span className="text-sm text-slate-600">- {doc.file.name}</span>
                          <i className="ri-check-line text-emerald-600 ml-auto"></i>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {extractionResults.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <h4 className="font-bold mb-3">Extracted Data:</h4>
                    <div className="space-y-2">
                      {extractionResults.map((result, idx) => (
                        <div key={idx} className="text-sm text-slate-700">{result}</div>
                      ))}
                    </div>
                  </div>
                )}

                {uploadedDocs.length > 0 && (
                  <button
                    onClick={handleCalculate}
                    disabled={calculating}
                    className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {calculating ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Calculating...
                      </>
                    ) : (
                      <>
                        <i className="ri-calculator-line mr-2"></i>
                        Calculate Tax Liability
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {step === 'result' && result && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-white text-4xl"></i>
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-2">Your Tax Liability</h2>
                <p className="text-slate-600">Based on {result.taxYear} IRS tax brackets and {result.stateOfResidence} state tax</p>
              </div>

              {/* Main Result */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 mb-8 text-center border-2 border-emerald-200">
                <div className="text-sm font-semibold text-emerald-700 mb-2">Total Tax Liability (Federal + State)</div>
                <div className="text-5xl font-bold text-emerald-600 mb-4">{formatCurrency(result.finalTaxOwed)}</div>
                <div className="flex justify-center gap-8 text-sm mb-4">
                  <div>
                    <span className="text-slate-600">Effective Rate: </span>
                    <span className="font-bold text-slate-900">{formatPercentage(result.effectiveTaxRate)}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Marginal Rate: </span>
                    <span className="font-bold text-slate-900">{formatPercentage(result.marginalTaxRate)}</span>
                  </div>
                </div>
                {result.federalTaxWithheld > 0 && (
                  <div className={`mt-4 pt-4 border-t border-emerald-200 ${result.federalRefundOrOwed >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                    <div className="text-sm font-medium mb-1">
                      {result.federalRefundOrOwed >= 0 ? 'Estimated Federal Refund' : 'Additional Federal Tax Owed'}
                    </div>
                    <div className="text-3xl font-bold">
                      {formatCurrency(Math.abs(result.federalRefundOrOwed))}
                    </div>
                  </div>
                )}
              </div>

              {/* Breakdown */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-4">Income Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Gross Income</span>
                      <span className="font-semibold">{formatCurrency(result.grossIncome)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Additional Income</span>
                      <span className="font-semibold">{formatCurrency(result.additionalIncome)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Pre-Tax Deductions</span>
                      <span className="font-semibold text-emerald-600">-{formatCurrency(result.preTaxDeductions)}</span>
                    </div>
                    <div className="pt-3 border-t border-slate-200 flex justify-between">
                      <span className="font-bold">Adjusted Gross Income</span>
                      <span className="font-bold">{formatCurrency(result.adjustedGrossIncome)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Deductions Used</span>
                      <span className="font-semibold text-emerald-600">-{formatCurrency(result.deductionUsed)}</span>
                    </div>
                    <div className="pt-3 border-t border-slate-200 flex justify-between">
                      <span className="font-bold">Taxable Income</span>
                      <span className="font-bold">{formatCurrency(result.taxableIncome)}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-4">Tax Breakdown</h3>
                  <div className="space-y-3">
                    <div className="font-semibold text-sm text-slate-700 mb-2">Federal Tax by Bracket:</div>
                    {result.breakdown.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-slate-600">{item.bracket} bracket</span>
                        <span className="font-semibold">{formatCurrency(item.tax)}</span>
                      </div>
                    ))}
                    {result.credits.length > 0 && (
                      <>
                        <div className="pt-3 border-t border-slate-200">
                          <div className="font-semibold mb-2 text-sm">Credits Applied:</div>
                          {result.credits.map((credit, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-slate-600">{credit.name}</span>
                              <span className="font-semibold text-emerald-600">-{formatCurrency(credit.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    <div className="pt-3 border-t border-slate-200 flex justify-between">
                      <span className="font-bold">Federal Tax</span>
                      <span className="font-bold">{formatCurrency(result.federalTaxOwed)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">State Tax ({result.stateOfResidence})</span>
                      <span className="font-bold">{formatCurrency(result.stateTax)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">Ready to Get Started?</h4>
                    <p className="text-slate-700 text-sm mb-4">
                      To proceed with our tax preparation services, please upload a valid government-issued ID for verification and compliance purposes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setStep('id-verification')}
                  className="flex-1 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all whitespace-nowrap"
                >
                  <i className="ri-shield-check-line mr-2"></i>
                  Continue to ID Verification
                </button>
                <button
                  onClick={() => {
                    setStep('input');
                    setResult(null);
                    setUploadedDocs([]);
                    setExtractionResults([]);
                  }}
                  className="px-6 py-4 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:border-slate-400 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Recalculate
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 'id-verification' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-blue-600 text-4xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">ID Verification Required</h2>
              <p className="text-slate-600">Upload a government-issued ID for security and compliance</p>
            </div>

            <div className="max-w-2xl mx-auto">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
                <div className="flex gap-3">
                  <i className="ri-shield-line text-amber-600 text-xl flex-shrink-0"></i>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">Why We Need This</h4>
                    <ul className="text-sm text-slate-700 space-y-1">
                      <li>• IRS compliance and identity verification</li>
                      <li>• Protect against fraud and identity theft</li>
                      <li>• Secure your tax information</li>
                      <li>• Required by federal regulations</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-id-card-line text-blue-600 text-3xl"></i>
                </div>
                <h3 className="font-bold text-lg mb-2">Upload Your ID</h3>
                <p className="text-slate-600 mb-6">Accepted: Driver's License, Passport, State ID</p>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleIdUpload}
                  className="hidden"
                  id="id-upload"
                />
                <label
                  htmlFor="id-upload"
                  className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-upload-2-line mr-2"></i>
                  Select ID Document
                </label>
                {uploading && (
                  <div className="mt-4">
                    <div className="flex items-center justify-center gap-2 text-blue-600">
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Verifying your ID...</span>
                    </div>
                  </div>
                )}
              </div>

              {idDocument && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <i className="ri-file-text-line text-emerald-600 text-xl"></i>
                    <span className="text-sm font-medium">{idDocument.name}</span>
                    <i className="ri-check-line text-emerald-600 ml-auto"></i>
                  </div>
                </div>
              )}

              <button
                onClick={() => setStep('result')}
                className="w-full py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:border-slate-400 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Results
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
